import { TWIN_TYPE } from './config';
import { TWIN_TYPE_LABELS } from './twinLinkage';

/**
 * The words for a multiple birth — TGCLOUD-1692.
 *
 * ONE module, because before this the product had no vocabulary for a set of three at
 * all. A triplet read "Identical twin of UPN 4, UPN 5"; the sidebar card was headed
 * TWIN STATUS; the clear button said "Not a twin"; and `deleteConsequences` counted
 * "twin links". Four places describing the same set, none of them able to say
 * "triplet". Wording that lives next to the thing it describes drifts, so it lives
 * here instead and the sidebar, the context menu and the delete dialog all read it.
 *
 * Two rules this module exists to hold:
 *
 * 1. **The noun follows the SET SIZE**, never a stored field. `siblingType` is a
 *    denormalisation of size and has been observed carrying `twin` for a set of three
 *    (the context-menu path) and `quadruplet` for a set of five (the old count
 *    mapping). Deriving the word from the members actually present cannot drift.
 *
 * 2. **The zygosity adjective follows the PAIR, not the set.** A set of three or more
 *    does not have one zygosity — `twinRelations[].type` is per pair, and the common
 *    clinical case is one identical pair alongside a third who is not. So anything
 *    above a pair may need to say two different things in one sentence, and the
 *    wording has to be built from segments rather than a single label lookup.
 */

/** Singular noun by set size. Above six there is no ordinary English word we should
 *  put in a clinical record, so `multipleBirthPhrase` falls back to a count. */
export const MULTIPLE_BIRTH_NOUN = Object.freeze({
  2: 'twin',
  3: 'triplet',
  4: 'quadruplet',
  5: 'quintuplet',
  6: 'sextuplet'
});

/** Card header and section title. "Multiple birth" is the umbrella term that stays
 *  correct at every size, which "Twin status" does not. */
export const MULTIPLE_BIRTH_HEADER = 'Multiple birth';

/**
 * The ADJECTIVE form of a zygosity, for sentences that already carry the noun.
 *
 * `TWIN_TYPE_LABELS` gives the standalone label ("Identical twin"), which cannot be
 * reused above a pair — "Identical twin of A, B" for a triplet is the defect. Both
 * spellings must keep agreeing, so they sit one import apart.
 *
 * `unknown` has no adjective on purpose: "unknown-zygosity triplets" is worse English
 * than the phrase the builders below use for it, and `singleton` never reaches display
 * because it means "not a multiple" rather than a kind of one.
 */
export const ZYGOSITY_ADJECTIVE = Object.freeze({
  [TWIN_TYPE.monozygotic]: 'identical',
  [TWIN_TYPE.dizygotic]: 'non-identical',
  [TWIN_TYPE.unknown]: null
});

/** Written-out small numbers. "The other two will remain twins" reads as prose;
 *  "The other 2 will remain twins" reads as a form field. */
const NUMBER_WORD = Object.freeze({
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six'
});

const numberWord = (n) => NUMBER_WORD[n] || String(n);

/** Singular noun, or null above six. */
export const multipleBirthNoun = (size) => MULTIPLE_BIRTH_NOUN[size] || null;

/** Plural noun, or null above six. Every noun here pluralises with a plain "s". */
export const multipleBirthPlural = (size) => {
  const noun = multipleBirthNoun(size);
  return noun ? `${noun}s` : null;
};

/**
 * How to refer to the set as a whole: "triplets", or "a multiple birth of 7".
 *
 * The fallback is deliberately a count rather than "septuplets". Sets that large are
 * vanishingly rare and almost always a data-entry error; printing the count invites
 * the reader to check it, where a confident Latin noun invites them to believe it.
 */
export const multipleBirthPhrase = (size) =>
  multipleBirthPlural(size) || `a multiple birth of ${size}`;

/** "a", "a and b", "a, b and c" — an Oxford-comma-free list, matching how the rest of
 *  the sidebar reads. */
export const formatNameList = (names) => {
  const list = (names || []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

/**
 * One clause describing this person's relation to the partners sharing one zygosity.
 *
 * "identical to UPN 4 - Jane Doe", or "zygosity unknown with UPN 5" — `with` rather
 * than `to` for the unknown case, because "unknown to" would read as not knowing the
 * person.
 */
const zygositySegment = ({ type, labels }) => {
  const adjective = ZYGOSITY_ADJECTIVE[type];
  const list = formatNameList(labels);
  return adjective
    ? `${adjective} to ${list}`
    : `zygosity unknown with ${list}`;
};

/**
 * The at-rest status line for a member of a multiple birth.
 *
 * @param {number} size   how many people are in the set, INCLUDING this one
 * @param {Array}  groups [{ type, labels }] — this person's confirmed partners,
 *                        grouped by the zygosity of the pair, already display-labelled
 * @returns {?string} null when this person is not in a set
 *
 * A PAIR keeps its existing wording exactly ("Identical twin of UPN 4 - Jane Doe").
 * That is not laziness: every twin already recorded reads that way today, and changing
 * it would churn the sidebar for the overwhelmingly common case in order to serve the
 * rare one. Three and above get the new forms, because they currently have no correct
 * form at all.
 */
export const multipleBirthStatusLine = ({ size, groups }) => {
  const present = (groups || []).filter((g) => g && (g.labels || []).length);
  if (!present.length) return null;

  // A pair: unchanged from TGCLOUD-1643.
  if (size === 2) {
    const { type, labels } = present[0];
    return `${TWIN_TYPE_LABELS[type] || 'Twin'} of ${formatNameList(labels)}`;
  }

  const phrase = multipleBirthPhrase(size);
  const mixed = present.length > 1;
  const named = multipleBirthPlural(size);

  // Above six, always the segment form — "One of an identical multiple birth of 7"
  // is not a sentence anybody should have to read.
  if (mixed || !named) {
    return `One of ${phrase} — ${present.map(zygositySegment).join('; ')}`;
  }

  const { type, labels } = present[0];
  const adjective = ZYGOSITY_ADJECTIVE[type];
  const list = formatNameList(labels);
  return adjective
    ? `One of ${adjective} ${named}, with ${list}`
    : `One of ${named} of unknown zygosity, with ${list}`;
};

/**
 * The label on the control that takes this person OUT of the set.
 *
 * Size-aware because the operation genuinely differs. Leaving a pair dissolves it, so
 * "Not a twin" is the whole truth. Leaving a triplet does not dissolve anything — the
 * other two stay twins — so the label names what this person stops being, and
 * `remainderConsequence` supplies the rest.
 */
export const notAMultipleLabel = (size) => {
  const noun = multipleBirthNoun(size);
  return noun ? `Not a ${noun}` : 'Remove from this set';
};

/**
 * What happens to everyone else, stated before the click rather than discovered after.
 *
 * Returns null for a pair: there is no remainder to describe, and a sentence saying so
 * would be noise. Same reasoning as `deleteConsequences`, which drops any bullet that
 * would read "0".
 */
export const remainderConsequence = (size) => {
  const remaining = Number(size) - 1;
  if (!Number.isFinite(remaining) || remaining < 2) return null;
  const plural = multipleBirthPlural(remaining);
  return plural
    ? `The other ${numberWord(remaining)} will remain ${plural}.`
    : `The other ${remaining} will remain ${multipleBirthPhrase(remaining)}.`;
};

/**
 * The delete-dialog consequence of removing a member of a set of THREE OR MORE.
 *
 * `deleteConsequences` counts relations — "remove 2 twin links" — which is an
 * implementation detail rather than a consequence, and never names the set. Above a pair
 * what a clinician needs to know is what SURVIVES: deleting one of three co-multiples
 * leaves two twins, not three singletons.
 *
 * Returns null below three, so the caller keeps its existing "remove 1 twin link". A pair
 * has no remainder to describe and the count is not misleading at that size — the same
 * call made for the status line, and it leaves three existing tests standing.
 */
export const setDepartureLine = (size) => {
  if (!Number.isFinite(Number(size)) || Number(size) < 3) return null;
  const noun = multipleBirthNoun(size);
  const remaining = size - 1;
  const plural =
    multipleBirthPlural(remaining) || multipleBirthPhrase(remaining);
  return `remove them from the ${
    noun || 'multiple birth'
  } set, leaving the other ${numberWord(remaining)} as ${plural}`;
};
